<template>
  <v-card class="rounded-lg my-5 pb-5" elevation="4" v-if="station" color="pwt_snow">
    <v-card-title
      class="pa-5"
      style="
        color: rgb(var(--v-theme-pwt_black));
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 18px; /* 112.5% */
        letter-spacing: -0.16px;
      "
    >
      Estimate details
    </v-card-title>
    <v-card-text class="px-5 pt-5">
      <v-select
        v-model="connector"
        return-object
        :items="connectorList"
        :item-title="(item) => item.displayName + ' ' + item.ratingDisplayStr"
        :item-props="(item) => ({ 'prepend-avatar': item.getPlugIconSrc })"
        @update:model-value="calcDetails"

        bg-color="transparent"
      >
        <template v-slot:prepend-inner>
          <v-avatar :image="connector?.getPlugIconSrc" />
        </template>
      </v-select>
    </v-card-text>
    <!-- display data if compatible with this charging station -->
    <template v-if="isCompatibleWithStation !== 'incompatible'">
      <v-card-text class="pb-0">
        <v-row no-gutters class="px-2">
          <v-col cols="6" class="d-flex flex-column align-start justify-center pr-2">
            <span
              style="font-size: 12px; font-weight: 300; line-height: 16px; letter-spacing: -0.12px"
            >
              Charge from...
            </span>
            <span class="text-h6"> {{ range[0] }}% </span>
          </v-col>
          <v-col cols="6" class="d-flex flex-column align-start justify-center pl-2">
            <span
              style="font-size: 12px; font-weight: 300; line-height: 16px; letter-spacing: -0.12px"
            >
              Charge to...
            </span>
            <span class="text-h6"> {{ range[1] }}% </span>
          </v-col>
        </v-row>

        <v-range-slider
          v-model="range"
          min="1"
          max="100"
          track-color="grey-lighten-2"
          @end="calcDetails"
          hid-details
          dense
          color="primary"
          thumb-size="16"
          track-size="6"
          step="1"
        />
      </v-card-text>
      <v-card-text class="pt-1">
        <v-row class="mb-2 px-2">
          <v-col cols="4" class="pr-2">
            <v-row
              no-gutters
              class="pa-3"
              style="border-radius: 8px; border: 1px solid rgb(var(--v-theme-pwt_off_white))"
            >
              <v-icon color="primary" class="mr-3"> mdi-clock-outline </v-icon>
              <span> {{ station.hasHydrogen ? 'Refuel' : 'Charge' }} for {{ chargingTime }} </span>
            </v-row>
          </v-col>
          <v-col cols="4" class="px-2">
            <v-row
              no-gutters
              class="pa-3"
              style="border-radius: 8px; border: 1px solid rgb(var(--v-theme-pwt_off_white))"
            >
              <v-icon color="primary" class="mr-3"> custom:circleCurrencyIcon </v-icon>
              <span>
                around ${{
                  chargingCost.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </span>
            </v-row>
          </v-col>
          <v-col cols="4" class="pl-2">
            <v-row
              no-gutters
              class="pa-3"
              style="border-radius: 8px; border: 1px solid rgb(var(--v-theme-pwt_off_white))"
            >
              <v-icon color="primary" class="mr-3"> custom:offlineBolt </v-icon>
              <span>
                {{
                  (station.hasHydrogen ? `${fuelAdded}kgs` : `${energyAdded}kWh`) +
                  ` (+${percentageCharged}%)`
                }}
              </span>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </template>
    <!-- display message if not compatible with this charging station -->
    <v-card-text v-else class="pl-6">
      Your selected vehicle is not compatible with your selected connector.
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import Charger from '@/classes/charger_classes/charger'
import Connector from '@/classes/charger_classes/connector'
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import { GettersTypes, type State } from '@/store/store_types'
import type { isCompatibleReturn } from '@/types/sheared_local_types'
import { kWhToHydrogenKgs } from '@/utils/hydrogenUtils'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

// const { connector } = defineProps<{ connector?: Connector }>()

const store = useStore<State>()

const range = ref([10, 90])
const energyAdded = ref(0)
const fuelAdded = ref(0)
const percentageCharged = ref(0)
const chargingTime = ref('')
const chargingCost = ref(0)
const isCompatibleWithStation = ref<isCompatibleReturn>('incompatible')

const station = computed<Charger | undefined>(() => store.getters[GettersTypes.selectedChargerData])
const vehicle = computed<Vehicle | undefined>(() => {
  if (store.getters[GettersTypes.selectedTripData]) {
    return store.getters[GettersTypes.selectedTripData].vehicle
  }
  return store.getters[GettersTypes.selectedVehicleData]
})
const connectorList = computed<Connector[]>(() => {
  const tempCompatibleConnectorsArray: Connector[] = []
  const tempIncompatibleConnectorsArray: Connector[] = []
  if (vehicle.value) {
    station.value?.allConnectors.forEach((connector) => {
      const isCompatible = connector.isCompatible(vehicle.value!)
      if (isCompatible === 'compatible' || isCompatible === 'compatible with cable only') {
        const isUsed = tempCompatibleConnectorsArray.find(
          (c) =>
            c.standard === connector.standard &&
            c.format === connector.format &&
            c.maxElectricPower === connector.maxElectricPower,
        )
        if (!isUsed) {
          tempCompatibleConnectorsArray.push(connector)
        }
      } else {
        const isUsed = tempIncompatibleConnectorsArray.find(
          (c) =>
            c.standard === connector.standard &&
            c.format === connector.format &&
            c.maxElectricPower === connector.maxElectricPower,
        )
        if (!isUsed) {
          tempIncompatibleConnectorsArray.push(connector)
        }
      }
    })
  }
  return [
    ...tempCompatibleConnectorsArray.sort((a, b) => b.maxElectricPower! - a.maxElectricPower!),
    ...tempIncompatibleConnectorsArray.sort((a, b) => b.maxElectricPower! - a.maxElectricPower!),
  ]
})
const connector = ref<Connector | undefined>(connectorList.value?.[0])

const min = computed(() => store.state.SOCMin)
const max = computed(() => store.state.SOCMax)
const defaultPublicCostPerKWh = computed(() => store.state.defaultPublicCostPerKWh)
const defaultCostPerMinDC = computed(() => store.state.defaultCostPerMinDC)

function calcDetails() {
  if (!station.value || !vehicle.value) {
    isCompatibleWithStation.value = 'incompatible'
    return
  }
  const isCompatible = station.value.isCompatible(vehicle.value)

  isCompatibleWithStation.value = isCompatible

  if (isCompatible === 'incompatible') {
    return
  }

  if (!range.value[0] || !range.value[1]) return

  const details = connector.value?.getChargingEstimateDetails({
    vehicle: vehicle.value,
    range: {
      min: range.value[0],
      max: range.value[1],
    },
    defaultCostPerKWh: defaultPublicCostPerKWh.value,
    defaultCostPerMinDC: defaultCostPerMinDC.value,
    defaultCostPerKgHydrogen: store.state.hydrogen_cost_per_kg ?? 0,
  })

  if (!details) {
    isCompatibleWithStation.value = 'incompatible'
    return
  }

  energyAdded.value = details.energyAdded
  if (station.value.hasHydrogen) {
    fuelAdded.value = kWhToHydrogenKgs(details.energyAdded)
  }
  chargingCost.value = details.chargingCost
  chargingTime.value = details.chargingTime
  percentageCharged.value = details.percentageCharged
}

watch(
  () => connector,
  () => {
    calcDetails()
  },
)

watch(
  () => vehicle,
  () => {
    calcDetails()
  },
)

onMounted(() => {
  nextTick(() => {
    if (min.value && max.value) {
      range.value = [min.value * 100, max.value * 100]
    }
    nextTick(() => {
      calcDetails()
    })
  })
})
</script>
